import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CommonButton from '../../../../components/common-button/CommonButton';
import CommonModal from '../../../../components/common-modal/CommonModal';
import { EMPTY_IMAGE } from '../../../../ad-images/consumer-ad-images/recreate-images/output/card/FrameCard';
import PromptModal from '../prompt-modal/PromptModal';
import FrameSlider from '../slider/FrameSlider';
import UploadImageButton from '../upload-image-button/UploadImageButton';
import PreviewSlide from '../slider/PreviewSlide';
import { PROMPT_TYPE } from '../../../../constants';

import scss from './PhotoPreview.module.scss';

function PhotoPreview({ isOpen, onClose, imagePreview, onDownload, onLike, onShowSourceImage, onRevise }) {
  const { t } = useTranslation('id8');

  const { frameData, initialIndex } = imagePreview || {};

  const [revisePhoto, setRevisePhoto] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(initialIndex);

  const currentImageData = frameData?.images?.[currentImageIndex] ?? EMPTY_IMAGE;

  const handleReviseModalOpen = () => {
    setRevisePhoto(true);
  };

  const handleCloseReviseModal = () => {
    setRevisePhoto(null);
  };

  const handleRevise = async (values) => {
    handleCloseReviseModal();
    await onRevise({ imageId: currentImageData.id, description: values.description, file: values.logo });
    setCurrentImageIndex(0);
  };

  const handleSwipe = (_, next) => {
    setCurrentImageIndex(next);
  };

  const handleShowSourceImage = () => {
    onShowSourceImage(frameData);
  };

  useEffect(() => {
    setCurrentImageIndex(initialIndex);
  }, [initialIndex]);

  return (
    <>
      <CommonModal isOpen={isOpen} onClose={onClose} isAnimated>
        <div className={scss.wrapper}>
          <FrameSlider
            images={frameData?.images}
            initialIndex={initialIndex}
            onLike={onLike}
            onDownload={onDownload}
            onSwipe={handleSwipe}
            isGenerating={frameData?.generating}
            sliderWrapperClassName={scss.sliderWrapper}
            currentIndex={currentImageIndex}
            Slide={PreviewSlide}
            {...(frameData?.promptType === PROMPT_TYPE.RECREATE ? { onShowSourceImage: handleShowSourceImage } : {})}
            contrastedArrows
            enableArrowNavigation
          />

          <div className={scss.content}>
            <div className={scss.header}>
              <span className={scss.title}>
                <span>{currentImageData.ideaHeadline}</span>
                <span>{currentImageData.copy}</span>
                <span>{currentImageData.advertisingStatement}</span>
              </span>

              {onRevise ? (
                <CommonButton onClick={handleReviseModalOpen} isLoading={frameData?.generating} maxWidth={192}>
                  {t('reviseImage')}
                </CommonButton>
              ) : null}
            </div>

            <div className={scss.textContent}>
              {currentImageData.campaignIdea ? (
                <span className={scss.labeledText}>
                  <b>{t('campaignIdea')}: </b>
                  {currentImageData.campaignIdea}
                </span>
              ) : null}
              {currentImageData.strategyInsight ? (
                <span className={scss.labeledText}>
                  <b>{t('strategyInsight')}: </b>
                  {currentImageData.strategyInsight}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </CommonModal>

      <PromptModal
        isOpen={revisePhoto}
        onClose={handleCloseReviseModal}
        onCancel={handleCloseReviseModal}
        onSubmit={handleRevise}
        title={t('addYourIdea')}
        AdditionalFields={UploadImageButton}
        additionalInitialFields={{ logo: null }}
      />
    </>
  );
}

export default memo(PhotoPreview);
